/**
 * KI-Berater Screen — Export game state for LLM analysis, import optimized plans.
 */
import { fixture, mapData } from '../data/fixture';
import type { PlanEntryDTO } from '../data/fixture';
import { state } from '../main';
import { setLocalPlan } from './planning';
import { submitPlan } from '../api';
import type { PlanSubmitAction } from '../api';
import { t } from '../i18n';

// ── Export builders ────────────────────────────────────────────────────────

function buildRunnerExport() {
  return (fixture.player.runners ?? []).map(r => ({
    name: r.name, skills: r.skills,
    str: r.str, int: r.int, stl: r.stl, tch: r.tch, cha: r.cha, con: r.con,
    hp: r.hp, heat: r.heat, loy: r.loy, stress: r.stress,
    status: r.status, team: r.team, salary: r.salary,
  }));
}

function buildBuildingExport() {
  return {
    buildings: (fixture.player.buildings ?? []).map(b => ({
      name: b.name, type: b.type, sector: b.sector,
      owned: b.owned, income: b.income, heat: b.heat, isFront: b.isFront,
    })),
    sectors: (fixture.sectors ?? []).map(s => ({
      sectorId: s.sectorId, heat: s.heat, owner: s.owner,
    })),
  };
}

function buildEconomyExport() {
  const p = fixture.player;
  return {
    cleanCash: p.cleanCash, dirtyCash: p.dirtyCash,
    netWorth: p.netWorth, federalHeat: p.federalHeat,
    dailyBalance: p.dailyBalance ?? null,
    subsidiaries: (p.subs ?? []).map(s => ({
      name: s.name, tier: s.tier, risk: s.risk, dirty: s.dirty, frozen: s.frozen,
    })),
    storage: p.storage ?? [],
  };
}

function buildDiplomacyExport() {
  const d = fixture.player.diplomacy;
  if (!d) return null;
  return {
    naps: d.naps, rivals: d.rivals, intel: d.intel,
    spies: d.spies, betrayalCount: d.betrayalCount,
  };
}

function buildGazetteExport() {
  // Every one of these is an i18n key since engine v1.99.28 — headline was
  // already resolved here, lead/impact were not, so the advisor was being fed
  // raw "gazette.ambient.*.lead" strings. date arrives as German prose
  // ("Tag 7") regardless of UI language; the day is in the fixture, so render
  // it the same way the gazette screen does.
  const day = String(fixture.day ?? 0);
  return (fixture.gazette ?? []).map(g => ({
    headline: t(g.headline, g.headlineVars),
    category: g.category,
    date: t('gazette.article_day', { day }),
    lead: t(g.lead ?? '', g.headlineVars),
    impact: t(g.impact ?? '', g.headlineVars),
    type: g.type,
  }));
}

function buildWorldBuildingsExport() {
  const scouted = new Set(fixture.player.scoutedSectors ?? []);
  if (!mapData?.worldBuildings) return { scoutedSectors: [...scouted], buildings: [] };

  const buildings = mapData.worldBuildings
    .filter(b => scouted.size === 0 || scouted.has(b.sector_id))
    .map(b => ({
      nodeId: b.node_id, name: b.display_name, sector: b.sector_id,
      category: b.category, income: b.income_base, rent: b.rent,
      buyPrice: b.buy_price, defense: b.defense_value,
      illegal: b.illegal, isFront: b.is_front_company,
      synergyTags: b.synergy_tags,
    }));
  return { scoutedSectors: [...scouted], buildings };
}

function buildPlanExport() {
  return fixture.player.plan ?? [];
}

function buildFullExport() {
  return {
    meta: {
      corpName: state.corpName, day: fixture.day,
      cycle: state.cycle, cycleMax: state.cycleMax,
      exportedAt: new Date().toISOString(),
    },
    runners: buildRunnerExport(),
    ...buildBuildingExport(),
    economy: buildEconomyExport(),
    diplomacy: buildDiplomacyExport(),
    plan: buildPlanExport(),
    gazette: buildGazetteExport(),
    worldBuildings: buildWorldBuildingsExport(),
    leaderboard: (fixture.leaderboard ?? []).map(l => ({
      rank: l.rank, name: l.name, netWorth: l.netWorth,
      territory: l.territory, isMe: l.isMe, eliminated: l.eliminated,
    })),
    availableActions: (fixture.player.actions ?? []).map(a => ({
      id: a.id, cat: a.cat, label: a.label, cost: a.cost, durationMin: a.durationMin,
    })),
  };
}

// ── System prompt ──────────────────────────────────────────────────────────

function buildSystemPrompt(): string {
  const actions = (fixture.player.actions ?? []).map(a => a.id).join(', ');
  const runners = (fixture.player.runners ?? []).map(r => `${r.name} (HP:${r.hp} Heat:${r.heat} Loy:${r.loy} Stress:${r.stress})`).join(', ');
  const scouted = (fixture.player.scoutedSectors ?? []).join(', ');
  const gazetteLines = (fixture.gazette ?? []).slice(0, 5).map(g => `- [${g.category}] ${t(g.headline, g.headlineVars)}`).join('\n');
  const planLines = (fixture.player.plan ?? []).map(p => `- ${p.runner}: ${p.action} → ${p.sector}`).join('\n');
  return `${t('llm.prompt_intro', { corp: state.corpName })}

${t('llm.prompt_situation')}
- ${t('llm.prompt_corp')}: ${state.corpName}
- Cycle: ${state.cycle + 1} ${t('llm.prompt_cycle_of')} ${state.cycleMax}
- Cash: ${state.cleanCash.toLocaleString()} (clean) / ${state.dirtyCash.toLocaleString()} (dirty)
- Net Worth: ${state.netWorth.toLocaleString()}
- Federal Heat: ${state.federalHeat}
- Runner: ${runners}
- ${t('llm.prompt_scouted')}: ${scouted || t('llm.prompt_none')}
${planLines ? `\n${t('llm.prompt_current_plan')}\n${planLines}` : ''}
${gazetteLines ? `\n${t('llm.prompt_recent_news')}\n${gazetteLines}` : ''}

${t('llm.prompt_actions')}
${actions}

${t('llm.prompt_format')}

${t('llm.prompt_closing')}`;
}

// ── Clipboard helper ───────────────────────────────────────────────────────

function copyToClipboard(text: string, btn: HTMLElement): void {
  const orig = btn.textContent;
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text).then(() => {
      btn.textContent = t('llm.copied');
      setTimeout(() => { btn.textContent = orig; }, 1500);
    });
  } else {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.cssText = 'position:fixed;opacity:0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    btn.textContent = t('llm.copied');
    setTimeout(() => { btn.textContent = orig; }, 1500);
  }
}

// ── Render ──────────────────────────────────────────────────────────────────

export function renderLlm(): HTMLElement {
  const root = document.createElement('div');
  root.className = 'screen';

  const style = document.createElement('style');
  style.textContent = `
    .llm-section { margin-bottom:24px; }
    .llm-section-title { font-size:14px; font-weight:700; color:var(--cyan); text-transform:uppercase;
      letter-spacing:1px; margin-bottom:12px; font-family:var(--mono); }
    .llm-hint { font-size:12px; color:var(--text-lo); margin-bottom:8px; }
    .llm-prompt-box { background:var(--surface-frost); backdrop-filter:blur(3px); -webkit-backdrop-filter:blur(3px); border:1px solid var(--line); border-radius:4px;
      padding:12px; font-family:var(--mono); font-size:12px; color:var(--text-mid);
      white-space:pre-wrap; word-break:break-word; max-height:300px; overflow-y:auto; line-height:1.5; }
    .llm-block { background:var(--surface-frost); backdrop-filter:blur(3px); -webkit-backdrop-filter:blur(3px); border:1px solid var(--line); border-radius:4px;
      margin-bottom:8px; overflow:hidden; }
    .llm-block-header { display:flex; align-items:center; justify-content:space-between;
      padding:8px 12px; cursor:pointer; }
    .llm-block-header:hover { background:rgba(var(--ac),0.04); }
    .llm-block-title { font-size:13px; font-weight:600; color:var(--text-hi); font-family:var(--mono); }
    .llm-block-meta { font-size:11px; color:var(--text-lo); font-family:var(--mono); }
    .llm-block-body { display:none; border-top:1px solid var(--border); }
    .llm-block-body.open { display:block; }
    .llm-json { background:var(--bg); padding:12px; font-family:var(--mono); font-size:11px;
      color:var(--green); white-space:pre-wrap; word-break:break-word; max-height:400px; overflow-y:auto; line-height:1.4; }
    /* .llm-copy-btn → shared .btn.sm · .llm-import-btn → shared .btn.cyan (markers kept) */
    /* .llm-import-area → shared .field; only the green JSON text + sizing stay local */
    .llm-import-area { width:100%; min-height:200px; resize:vertical; color:var(--green); line-height:1.5; }
    .llm-import-actions { display:flex; gap:8px; margin-top:8px; align-items:center; }
    .llm-status { font-size:12px; font-family:var(--mono); margin-left:8px; }
    .llm-status.ok { color:var(--green); }
    .llm-status.error { color:var(--red); }
  `;
  root.appendChild(style);

  // ── Title ──
  const title = document.createElement('div');
  title.className = 'screen-title';
  title.textContent = t('llm.title');
  root.appendChild(title);

  // ── A) System Prompt ──
  const promptSection = document.createElement('div');
  promptSection.className = 'llm-section';
  const promptText = buildSystemPrompt();
  promptSection.innerHTML = `
    <div class="llm-section-title">${t('llm.section_prompt')}</div>
    <div class="llm-hint">${t('llm.prompt_hint')}</div>
    <div style="display:flex;justify-content:flex-end;margin-bottom:6px">
      <button class="llm-copy-btn btn sm" id="llm-copy-prompt">${t('llm.btn_copy')}</button>
    </div>
    <div class="llm-prompt-box">${escapeHtml(promptText)}</div>
  `;
  root.appendChild(promptSection);

  // ── B) Export Blocks ──
  const exportSection = document.createElement('div');
  exportSection.className = 'llm-section';
  exportSection.innerHTML = `<div class="llm-section-title">${t('llm.section_export')}</div>`;

  const blocks: { key: string; label: string; build: () => unknown }[] = [
    { key: 'runners', label: t('llm.block_runners'), build: buildRunnerExport },
    { key: 'buildings', label: t('llm.block_buildings'), build: buildBuildingExport },
    { key: 'economy', label: t('llm.block_economy'), build: buildEconomyExport },
    { key: 'diplomacy', label: t('llm.block_diplomacy'), build: buildDiplomacyExport },
    { key: 'plan', label: t('llm.block_plan'), build: buildPlanExport },
    { key: 'gazette', label: t('llm.block_gazette'), build: buildGazetteExport },
    { key: 'world', label: t('llm.block_world'), build: buildWorldBuildingsExport },
    { key: 'full', label: t('llm.block_full'), build: buildFullExport },
  ];

  for (const { key, label, build } of blocks) {
    const data = build();
    const json = JSON.stringify(data, null, 2);
    const size = json.length > 1024 ? `${(json.length / 1024).toFixed(1)} KB` : `${json.length} B`;

    const block = document.createElement('div');
    block.className = 'llm-block';
    block.innerHTML = `
      <div class="llm-block-header" data-toggle="${key}">
        <span class="llm-block-title">${label}</span>
        <span style="display:flex;align-items:center;gap:8px">
          <span class="llm-block-meta">${size}</span>
          <button class="llm-copy-btn btn sm" data-copy="${key}">${t('llm.btn_copy')}</button>
        </span>
      </div>
      <div class="llm-block-body" id="llm-body-${key}">
        <div class="llm-json">${escapeHtml(json)}</div>
      </div>
    `;
    exportSection.appendChild(block);
  }
  root.appendChild(exportSection);

  // ── C) Plan Import ──
  const importSection = document.createElement('div');
  importSection.className = 'llm-section';
  importSection.innerHTML = `
    <div class="llm-section-title">${t('llm.section_import')}</div>
    <div class="llm-hint">${t('llm.import_hint')}</div>
    <textarea class="llm-import-area field" id="llm-import-json" placeholder='[{"runner":"...","action":"...","sector":"...","conditions":[]}]'></textarea>
    <div class="llm-import-actions">
      <button class="llm-import-btn btn cyan" id="llm-btn-validate">${t('llm.btn_validate')}</button>
      <button class="llm-import-btn btn cyan" id="llm-btn-apply" disabled>${t('llm.btn_apply')}</button>
      <span class="llm-status" id="llm-import-status"></span>
    </div>
  `;
  root.appendChild(importSection);

  // ── Wire events ──
  requestAnimationFrame(() => {
    // Copy prompt
    root.querySelector('#llm-copy-prompt')?.addEventListener('click', () => {
      copyToClipboard(promptText, root.querySelector('#llm-copy-prompt') as HTMLElement);
    });

    // Copy block buttons
    for (const { key, build } of blocks) {
      root.querySelector(`[data-copy="${key}"]`)?.addEventListener('click', (e) => {
        e.stopPropagation();
        const json = JSON.stringify(build(), null, 2);
        copyToClipboard(json, root.querySelector(`[data-copy="${key}"]`) as HTMLElement);
      });
    }

    // Toggle block bodies
    root.querySelectorAll('[data-toggle]').forEach(header => {
      header.addEventListener('click', () => {
        const key = (header as HTMLElement).dataset.toggle!;
        const body = root.querySelector(`#llm-body-${key}`);
        body?.classList.toggle('open');
      });
    });

    // Import: Validate
    const textarea = root.querySelector('#llm-import-json') as HTMLTextAreaElement;
    const applyBtn = root.querySelector('#llm-btn-apply') as HTMLButtonElement;
    const statusEl = root.querySelector('#llm-import-status') as HTMLElement;

    let parsedPlan: PlanEntryDTO[] | null = null;

    root.querySelector('#llm-btn-validate')?.addEventListener('click', () => {
      parsedPlan = null;
      applyBtn.disabled = true;
      statusEl.className = 'llm-status';

      const raw = textarea.value.trim();
      if (!raw) {
        statusEl.className = 'llm-status error';
        statusEl.textContent = t('llm.valid_error', { msg: 'empty input' });
        return;
      }

      let arr: unknown;
      try {
        arr = JSON.parse(raw);
      } catch {
        statusEl.className = 'llm-status error';
        statusEl.textContent = t('llm.valid_error', { msg: 'invalid JSON' });
        return;
      }

      if (!Array.isArray(arr)) {
        statusEl.className = 'llm-status error';
        statusEl.textContent = t('llm.valid_error', { msg: 'expected array' });
        return;
      }

      for (let i = 0; i < arr.length; i++) {
        const e = arr[i] as Record<string, unknown>;
        if (!e.runner || !e.action || !e.sector) {
          statusEl.className = 'llm-status error';
          statusEl.textContent = t('llm.valid_error', { msg: `entry ${i}: missing runner/action/sector` });
          return;
        }
      }

      parsedPlan = arr as PlanEntryDTO[];
      applyBtn.disabled = false;
      statusEl.className = 'llm-status ok';
      statusEl.textContent = t('llm.valid_ok', { count: String(parsedPlan.length) });
    });

    // Import: Apply — submit directly to DB via API
    applyBtn?.addEventListener('click', async () => {
      if (!parsedPlan) return;
      applyBtn.disabled = true;
      statusEl.className = 'llm-status';
      statusEl.textContent = t('llm.submitting');

      // Normalize plan entries
      const normalized = parsedPlan.map(e => ({
        ...e,
        conditions: e.conditions ?? [],
      }));

      // Also set in-memory so planning screen shows it
      setLocalPlan(normalized);

      // Session ID from global state
      const sessionId = state.sessionId ?? '';
      if (!sessionId) {
        statusEl.className = 'llm-status error';
        statusEl.textContent = t('llm.valid_error', { msg: 'no active session' });
        applyBtn.disabled = false;
        return;
      }

      const actions: PlanSubmitAction[] = normalized.map(e => ({
        runner:    e.runner,
        command:   e.action,
        target:    e.target ?? e.sector,
        condition: e.conditions.length > 0 ? e.conditions : undefined,
        fallback:  (e as any).else ?? undefined,
      }));

      const runners = fixture.player.runners ?? [];
      const usedRunnerIds = new Set(normalized.map(e => e.runner));
      const hoodId = runners.find(r => usedRunnerIds.has(r.id))?.id ?? runners[0]?.id ?? 'default';

      try {
        const result = await submitPlan(sessionId, { hood_id: hoodId, actions });
        if (result.ok) {
          statusEl.className = 'llm-status ok';
          statusEl.textContent = t('llm.submitted', { id: result.planId });
        } else {
          statusEl.className = 'llm-status error';
          statusEl.textContent = t('llm.valid_error', { msg: result.errors.join(', ') });
          applyBtn.disabled = false;
        }
      } catch (err) {
        statusEl.className = 'llm-status error';
        statusEl.textContent = t('llm.valid_error', { msg: String(err) });
        applyBtn.disabled = false;
      }
    });
  });

  return root;
}

// ── Utils ──────────────────────────────────────────────────────────────────

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
